import { lazy, Suspense, Component } from "react";
import type { ErrorInfo } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { SessionTimeout } from "@/components/SessionTimeout";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { LegalFooterLinks } from "@/components/LegalFooterLinks";

// RCA-fix (2026-06-19 v5): Do NOT create a module-level lazy() singleton for SophiaChat.
// React.lazy caches the import promise per factory call. If the chunk fails to load
// (ChunkLoadError on hard refresh), the cached rejected promise means EVERY subsequent
// attempt to render SophiaChat will throw — the error boundary's retry never succeeds.
// Fix: create a FRESH lazy() factory on each retry so React re-attempts the import.
const makeSophiaChatLazy = () =>
  lazy(() => import("@/components/SophiaChat").then((m) => ({ default: m.SophiaChat })));

/**
 * Task 3 fix (rev2): Per-component error boundary specifically for SophiaChat.
 * When the lazy chunk fails (stale deployment hash, transient network error,
 * or render error), this boundary isolates the failure so the rest of the app
 * remains fully functional.
 *
 * Recovery strategy (Issue 3 RCA 2026-06-19):
 * - Previous version returned null permanently → Sophia was gone until navigation.
 * - Now: schedules an automatic retry after RETRY_DELAY_MS.  On hard refresh the
 *   browser cache is empty; the first Suspense lazy-load may fail with a ChunkLoadError.
 *   The retry gives the browser a second attempt once chunks are being fetched again.
 * - Also resets on location.key change (navigation) as before.
 */
const SOPHIA_RETRY_DELAY_MS = 3000;

type SophiaErrorBoundaryState = {
  hasError: boolean;
  retryCount: number;
  // Each retry gets a FRESH lazy component so React.lazy re-attempts the import.
  // Storing it in state (not module-level) is essential: React.lazy caches the
  // import promise on a per-factory basis, so if we reuse the same lazy() constant
  // after a ChunkLoadError, the rejected promise is replayed and the retry throws
  // immediately again without hitting the network.
  SophiaChatLazy: ReturnType<typeof lazy>;
};

class SophiaErrorBoundary extends Component<
  { resetKey?: string },
  SophiaErrorBoundaryState
> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: { resetKey?: string }) {
    super(props);
    this.state = { hasError: false, retryCount: 0, SophiaChatLazy: makeSophiaChatLazy() };
  }

  static getDerivedStateFromError(): Partial<SophiaErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log but don't crash — Sophia is non-critical
    console.warn('[SophiaErrorBoundary] SophiaChat failed to render:', error.message, info.componentStack?.slice(0, 200));
    // Always retry with backoff. Use a FRESH lazy() factory each time so the
    // previously-failed import promise is NOT reused (see makeSophiaChatLazy above).
    // Backoff: attempt 0→3s, attempt 1→7s, attempt 2+→15s.
    const delay = this.state.retryCount === 0 ? 3000
                : this.state.retryCount === 1 ? 7000
                : 15000;
    this.retryTimer = setTimeout(() => {
      this.setState(prev => ({
        hasError: false,
        retryCount: prev.retryCount + 1,
        SophiaChatLazy: makeSophiaChatLazy(),  // fresh factory = new import attempt
      }));
    }, delay);
  }

  componentDidUpdate(prevProps: { resetKey?: string }) {
    // Reset on location/key change so Sophia gets another chance after navigation
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.setState({ hasError: false, retryCount: 0, SophiaChatLazy: makeSophiaChatLazy() });
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  render() {
    if (this.state.hasError) {
      // Return null while waiting for retry — Sophia absent but app fully functional
      return null;
    }
    const { SophiaChatLazy } = this.state;
    return (
      <Suspense fallback={null}>
        <SophiaChatLazy />
      </Suspense>
    );
  }
}

export const AppShell = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  // Pages that hide Sophia get symmetric padding for centered content
  const hideSophia = location.pathname.startsWith("/e-consent");

  // NP-455: min-h-viewport (100dvh with a 100vh fallback, see index.css)
  // instead of min-h-screen — on iPadOS Safari 100vh is the toolbar-retracted
  // viewport, so min-h-screen made short pages (e.g. the Disclosures & Consent
  // wizard step) scrollable with nothing but background below the content.
  return (
    <div className="min-h-viewport flex flex-col">
      <SessionTimeout />
      <Header />
      <div className="flex-1 flex">
        {/* Main content area - shrinks on desktop to accommodate chat panel, add mobile bottom padding */}
        <main className={`flex-1 flex flex-col px-4 sm:px-6 lg:px-8 py-6 pb-4 overflow-x-hidden max-w-full ${hideSophia ? '' : 'lg:pr-4'}`}>
          {/* NP-582 follow-up: the content takes the slack so the footer settles at the
              bottom of the column. Short pages — Contacts, Settings — used to leave the
              links floating just under the content with the rest of the screen empty
              below them, which read as something to click rather than boilerplate.
              Children stay inside a plain block so their own layout is unaffected. */}
          <div className="flex-1">
            {children}
          </div>
          {/* NP-582: inside <main>, at the end of the page column — NOT a sibling of
              this row, which holds the Sophia panel; a block underneath it left dead
              space below the chat. */}
          <footer className="mt-6 pb-20 md:pb-0">
            <LegalFooterLinks />
          </footer>
        </main>
        {/* Lazy-loaded chat panel — isolated error boundary prevents chunk/render failures
            from propagating to the app-level ErrorBoundary (Task 3 fix) */}
        {/* SophiaErrorBoundary now owns the Suspense + SophiaChat inside its render() */}
        <SophiaErrorBoundary resetKey={location.key} />
      </div>
      {/* Mobile bottom navigation */}
      <MobileBottomNav />
    </div>
  );
};
